import { useOrderMovements } from '@api/useOrderMovements'
import { GraviButton, Vertical } from '@gravitate-js/excalibrr'
import { type ReactNode, useCallback, useEffect } from 'react'
import { EbolTable } from '../DataTables/EbolTable'
import { useEbolMatchingContext } from '../../EbolMatchingContext'
import { BolObjectDetails } from '../DataTables/BolObjectDetails'
import { useDrawerType } from '../../Hooks/useDrawerType'
import { Result, Skeleton } from 'antd'
import { useApiClient } from '@/hooks/useApiClient'

const DrawerContent = (props: { top: ReactNode; bottom: ReactNode }) => (
  <Vertical style={{ rowGap: '1.4rem' }}>
    <div style={{ flex: 1, flexGrow: 1, height: '100%', maxHeight: '35vh' }}>{props.top}</div>
    <div style={{ flex: 1, flexGrow: 1, height: '100%' }}>{props.bottom}</div>
  </Vertical>
)

const UnrankedEbols = () => {
  const { useUnmatchedEbols } = useOrderMovements()
  const { data, isPending } = useUnmatchedEbols()
  return <EbolTable isLoading={isPending} ebolData={data} />
}

const MatchEbol = () => {
  const { selectedBolId, selectEbol } = useEbolMatchingContext()
  const { useRankedEbols } = useOrderMovements()
  const { data, isPending } = useRankedEbols(selectedBolId)
  const setSelectedEbol = useCallback((ebol: Gravitate.Ebol) => ebol._id && selectEbol(ebol._id), [selectEbol])
  return (
    <DrawerContent
      top={<BolObjectDetails data={data?.bol} isLoading={isPending} />}
      bottom={
        <EbolTable
          subtitle='Select an eBOL to match the selected BOL'
          bolData={data?.bol}
          ebolData={data?.ebols}
          isLoading={isPending}
          onEbolSelected={setSelectedEbol}
        />
      }
    />
  )
}

const UnmatchEbol = () => {
  const { selectedBolId } = useEbolMatchingContext()
  const { useMatchedEbol } = useOrderMovements()
  const { data, isPending } = useMatchedEbol(selectedBolId)
  return (
    <DrawerContent
      top={<BolObjectDetails title='Original BOL' data={data?.original_bol} isLoading={isPending} />}
      bottom={
        <EbolTable
          title='Matched eBOL'
          bolData={data?.original_bol}
          ebolData={data?.ebol ? [data.ebol] : []}
          isLoading={isPending}
          isReadonly
        />
      }
    />
  )
}

const ErrorPage = () => {
  const { close } = useEbolMatchingContext()
  return (
    <Result
      status='error'
      title='Error retrieving BOL data'
      extra={<GraviButton onClick={close}>Go Back</GraviButton>}
    />
  )
}

const JustMatched = () => {
  const { close } = useEbolMatchingContext()

  useEffect(() => {
    const timeout = setTimeout(() => {
      close()
    }, 2500)
    return () => {
      clearTimeout(timeout)
    }
  }, [close])

  return <Result status='success' title='The added mapping has caused one or more eBOLs to be automatically matched!' />
}

export const EbolDrawerContent = () => {
  const drawerType = useDrawerType()

  switch (drawerType) {
    case 'closed':
      return
    case 'loading':
      return <Skeleton active />
    case 'error':
      return <ErrorPage />
    case 'viewingAll':
      return <UnrankedEbols />
    case 'match':
      return <MatchEbol />
    case 'unmatch':
      return <UnmatchEbol />
    case 'justMatched':
      return <JustMatched />
  }
}
